<template>
  <div id="eocs-container" class="mx-4 mt-3">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <div
        class="d-flex flex-row justify-content-start align-items-center"
        style="width:220px;"
      >
        <div
          class="main-image-container bg-purple rounded d-flex justify-content-center align-items-center"
        >
          <img
            height="25"
            width="25"
            :src="require('../../assets/icons/users.svg')"
          />
        </div>
        <h3 class="mb-0 ml-3">Patients</h3>
      </div>
      <view-switch :options="viewOptions" v-model="currentView" />
      <div
        class="d-flex align-items-center justify-content-end"
        style="width:220px;"
      >
        <b-button
          v-if="hasCurrentUserPermissionAccess('patients', 'create')"
          variant="primary"
          class="smaller-button button-custom-blue"
          @click="addPatient"
          ><i class="fa fa-plus mr-2" aria-hidden="true"></i>
          New Episode of care
        </b-button>
      </div>
    </div>
    <eocs-index-filters
      v-model="filter"
      :hasFilters="hasFilters"
      :loading="loadingMorePatients"
      ref="filterHeight"
      :showing="showing"
    />
    <div
      id="eocs-list"
      class="clients-list"
      :class="hasFilters ? 'clients-list-filtered' : ''"
      :style="`--filter-height:` + filterHeight()"
    >
      <grid-view
        :exportFilter="exportFilter"
        :list="list"
        :listTotal="listTotal"
        :patientAvatars="patientAvatars"
        ref="clientGrid"
        v-show="currentView === 'grid'"
        :showing="showing && currentView === 'grid'"
        :assignedToMeValue="assignedToMe"
        @add-tab="data => $emit('add-tab', data)"
        @load-more="loadMoreEocs"
        @change-assignedToMe="changeAssignedToMEToggle"
      ></grid-view>
      <list-view
        :exportFilter="exportFilter"
        :list="list"
        :listTotal="listTotal"
        :orderBy="filter.orderBy"
        :orderByDescending="filter.orderByDescending"
        ref="clientList"
        v-show="currentView === 'list'"
        :showing="showing && currentView === 'list'"
        :assignedToMeValue="assignedToMe"
        @add-tab="data => $emit('add-tab', data)"
        @load-more="loadMoreEocs"
        @change-order="changeOrder"
        @change-assignedToMe="changeAssignedToMEToggle"
      >
      </list-view>
    </div>
    <create-eoc-from-patient-modal
      ref="createEocModal"
      @onAddEditEoc="onAddEditEoc"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import CreateEocFromPatientModal from "./CreateEocFromPatientModal";
import { formatEndDateSql, formatStartDateSql } from "../../helpers/date";
import EocsIndexFilters from "./EocsIndexFilters";
import GridView from "./EocsGridView";
import ListView from "./EocsListView";
import eocServer from "../../server/eoc";
import avatarServer from "../../server/avatar";
import ViewSwitch from "../controls/ViewSwitch.vue";

export default {
  name: "EocsIndex",
  components: {
    CreateEocFromPatientModal,
    EocsIndexFilters,
    GridView,
    ListView,
    ViewSwitch
  },
  props: ["showing"],
  data: () => ({
    currentView: "grid",
    filter: {
      searchPhrase: "",
      eocStatus: [
        {
          value: "Pending",
          label: "Pending"
        },
        {
          value: "Active",
          label: "Active"
        }
      ],
      assignedTo: [],
      dischargeReasonId: [],
      levelOfCareId: [],
      locations: [],
      placesOfService: [],
      badgeId: [],
      contactId: [],
      dateOfBirthFrom: null,
      dateOfBirthTo: null,
      startDateFrom: null,
      startDateTo: null,
      endDateFrom: null,
      endDateTo: null,
      orderBy: "status",
      orderByDescending: false
    },
    pagination: {
      page: 1,
      pageSize: 50
    },
    clientSearchChanged: false,
    loadingMorePatients: false,
    list: [],
    listTotal: 0,
    refetchEocs: true,
    patientAvatars: {}
  }),
  methods: {
    ...mapActions("client", [
      "fetchLevelsOfCare",
      "fetchGenders",
      "fetchPronouns",
      "fetchMaritalStatuses",
      "fetchRaces",
      "fetchEthnicities",
      "fetchLanguages",
      "fetchStates",
      "fetchAllergyTypes",
      "fetchAllergySeverities",
      "fetchRelationships",
      "fetchVitalSigns",
      "fetchDiagnosisCodeStatuses",
      "fetchAllergens",
      "fetchReactions",
      "fetchReviewTypes",
      "fetchInsuranceCoverages",
      "fetchMedicationRoutes",
      "fetchMeasurements",
      "fetchPlacesOfService"
    ]),
    ...mapActions("layout", ["toggleAssignToMeEocsIndex"]),

    async fetchInitialData() {
      this.fetchLevelsOfCare("name");
      this.fetchGenders();
      this.fetchPronouns();
      this.fetchPlacesOfService();
      this.fetchMaritalStatuses();
      this.fetchRaces();
      this.fetchEthnicities();
      this.fetchLanguages();
      this.fetchStates();
      this.fetchAllergyTypes();
      this.fetchAllergySeverities();
      this.fetchRelationships();
      this.fetchVitalSigns();
      this.fetchDiagnosisCodeStatuses();
      this.fetchAllergens();
      this.fetchReactions();
      this.fetchReviewTypes();
      this.fetchInsuranceCoverages();
      this.fetchMedicationRoutes();
      this.fetchMeasurements();
      this.filterHeight();
    },
    addPatient() {
      this.$refs["createEocModal"].show();
    },
    filterHeight() {
      return (
        (this.$refs.filterHeight
          ? this.$refs.filterHeight.$el.clientHeight
          : 0) + "px"
      );
    },
    onAddEditEoc() {
      this.resetList();
      this.changePage(1);
    },
    changeAssignedToMEToggle() {
      this.toggleAssignToMeEocsIndex();
      this.resetList();
      this.changePage(1);
      this.fetchPatients();
    },
    fetchPatients(instantReload = false) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      this.lastTimeout = setTimeout(
        async () => {
          const result = await eocServer.getAllEocMinimal({
            searchPhrase:
              this.filter.searchPhrase.length >= 3
                ? this.filter.searchPhrase
                : "",
            eocStatus: this.filter.eocStatus.map(s => s.value),
            assignedTo: this.filter.assignedTo.map(s => s.value),
            dischargeReasons: this.filter.dischargeReasonId.map(s => s.value),
            levelsOfCare: this.filter.levelOfCareId.map(s => s.value),
            contacts: this.filter.contactId.map(c => c.value),
            fromDateOfBirth: formatStartDateSql(this.filter.dateOfBirthFrom),
            toDateOfBirth: formatEndDateSql(this.filter.dateOfBirthTo),
            locations: this.filter.locations.map(s => s.value),
            fromStartDate: formatStartDateSql(this.filter.startDateFrom),
            toStartDate: formatEndDateSql(this.filter.startDateTo),
            fromEndDate: formatStartDateSql(this.filter.endDateFrom),
            toEndDate: formatEndDateSql(this.filter.endDateTo),
            hasAlert: this.filter.badgeId.find(b => !b.acronym) ? true : false,
            badges: this.filter.badgeId
              .filter(b => b.acronym)
              .map(b => b.value),
            page: this.pagination.page,
            pageSize: this.pagination.pageSize,
            orderBy: this.filter.orderBy,
            orderByDescending: this.filter.orderByDescending,
            assignedToMe: this.assignedToMe,
            placesOfService: this.filter.placesOfService.map(
              place => place.value
            )
          });
          let newList = [...this.list, ...result.episodesOfCare];
          this.list = newList.filter(
            (item, index) =>
              newList.findIndex(item2 => item2.id == item.id) === index
          );
          this.listTotal = result.count;
          this.downloadAvatars(result.episodesOfCare);
        },
        instantReload ? 0 : 1000
      );
    },
    async downloadAvatars(patientList) {
      let patientAvatars = { ...this.patientAvatars };
      for (let index = 0; index < patientList.length; index++) {
        const patient = patientList[index];
        if (patient.patient.avatarId) {
          try {
            patientAvatars[
              patient.patient.avatarId
            ] = await avatarServer.downloadUrl(patient.patient.avatarId);
          } catch {
            patientAvatars[
              patient.patient.avatarId
            ] = require(`../../assets/avatar-example.png`);
          }
          this.patientAvatars = { ...patientAvatars };
        }
      }
    },
    changePage(newPage, instantReload = false) {
      this.pagination.page = newPage;
      this.fetchPatients(instantReload);
      this.scrollGridToTop();
    },
    scrollGridToTop() {
      const grid = document.querySelector(".patient-list .b-grid");
      if (grid != null) {
        grid.scrollTop = 0;
      }
    },
    changeOrder(newOrder) {
      this.filter.orderBy = newOrder.orderBy;
      this.filter.orderByDescending = newOrder.orderByDescending;
      this.resetList();
      this.changePage(1, true);
    },
    resetList() {
      this.list = [];
      this.listTotal = 0;
      this.$refs.clientList?.changePage(0);
    },
    loadMoreEocs() {
      this.changePage(this.pagination.page + 1, true);
    }
  },
  computed: {
    ...mapGetters("layout", ["hasCurrentUserPermissionAccess"]),
    ...mapGetters("client", ["listeners"]),
    ...mapState("layout", {
      selectedLocation: state => state.selectedLocation,
      currentUser: state => state.currentUser,
      assignedToMe: state => state.assignedToMeEocsIndex
    }),
    hasFilters() {
      return (
        this.filter.assignedTo.length > 0 ||
        this.filter.eocStatus.length > 0 ||
        this.filter.levelOfCareId.length > 0 ||
        this.filter.locations.length > 0 ||
        this.filter.badgeId.length > 0 ||
        this.filter.dateOfBirthFrom ||
        this.filter.dateOfBirthTo ||
        this.filter.startDateFrom ||
        this.filter.startDateTo ||
        this.filter.endDateFrom ||
        this.filter.endDateTo ||
        this.filter.dischargeReasonId.length > 0
      );
    },
    exportFilter() {
      return {
        searchPhrase:
          this.filter.searchPhrase.length >= 3 ? this.filter.searchPhrase : "",
        levelOfCareIds: this.filter.levelOfCareId.map(l => l.value),
        dateOfBirthFrom: this.filter.dateOfBirthFrom,
        dateOfBirthTo: this.filter.dateOfBirthTo,
        locations: this.filter.locations.map(l => l.value),
        startDateFrom: this.filter.startDateFrom,
        startDateTo: this.filter.startDateTo,
        endDateFrom: this.filter.endDateFrom,
        endDateTo: this.filter.endDateTo,
        badgeIds: this.filter.badgeId.filter(b => b.acronym).map(b => b.value),
        hasAlert: this.filter.badgeId.find(b => !b.acronym)?.value.toString(),
        statusIds: this.filter.eocStatus.map(s => {
          if (s.value == "Pending") return 0;
          if (s.value == "Active") return 1;
          return 2;
        }),
        userIds: this.filter.assignedTo.map(s => s.value),
        dischargeReasonIds: this.filter.dischargeReasonId.map(s => s.value),
        orderBy: this.filter.orderBy,
        sort: this.filter.orderByDescending ? "DESC" : "ASC",
        locationIds: [this.selectedLocation],
        assignedToMe: this.assignedToMe.toString()
      };
    },
    viewOptions() {
      return [
        {
          value: "grid",
          label: "Grid",
          icon: "fas fa-th-large"
        },
        {
          value: "list",
          label: "List",
          icon: "fas fa-list-ul"
        }
      ];
    }
  },
  watch: {
    "filter.levelOfCareId": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.locations": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.placesOfService": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.badgeId": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.eocStatus": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.searchPhrase": function() {
      if (
        this.filter.searchPhrase.length == 0 ||
        this.filter.searchPhrase.length >= 3
      ) {
        this.resetList();
        this.changePage(1);
      }
    },
    "filter.dateOfBirthFrom": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.dateOfBirthTo": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.startDateFrom": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.startDateTo": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.endDateFrom": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.endDateTo": function() {
      this.resetList();
      this.changePage(1);
    },
    "filter.dischargeReasonId": function() {
      this.resetList();
      this.changePage(1);
    },
    "listeners.fetchEoc": function(newValue, oldValue) {
      if (newValue["all"] != oldValue["all"]) {
        this.refetchEocs = true;
      }
    },
    showing: {
      handler: async function(newValue) {
        if (newValue) {
          await this.fetchInitialData();
          this.refetchEocs = true;
        }
        if (newValue && this.refetchEocs) {
          await this.fetchInitialData();
          this.resetList();
          this.changePage(1, true);
          this.refetchEocs = false;
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.filter-select {
  height: 50px;
  width: 300px;
  padding-top: 5px;
}
.client-card {
  min-width: 180px;
  max-width: 180px;
  height: 315px;
}
.little-circle {
  width: 10px;
  height: 10px;
}
.little-circle-exclamation {
  width: 20px;
  height: 20px;
}
.little-circle-exclamation-container {
  width: 15px;
  height: 15px;
  margin-right: 5px;
}
.client-avatar {
  width: 100px;
  height: 100px;
  text-align: center;
}
.client-data {
  font-size: 0.8rem;
  word-break: break-all;
  overflow: hidden;
  text-overflow: clip;
}
.client-card:hover {
  cursor: pointer;
  box-shadow: 0 10px 10px -10px rgba(0, 0, 0, 0.5);
  transform: translateY(-3px);
  transition-duration: 0.3s;
  transition-property: transform;
}
.clients-list {
  height: calc(100vh - 240px - var(--filter-height));
  overflow-y: auto;
}
.border-yellow {
  border-color: #ffc107 !important;
}
.separator-or {
  margin-bottom: -13px;
}
</style>

<style>
.filter-select .vs__dropdown-toggle {
  height: 100%;
  border-radius: 0;
  border: 0;
}

.patient-badge {
  color: white;
  font-size: 11px;
  width: 40px;
  height: 16px;
  text-align: center;
  border-radius: 25px;
  line-height: 1.5;
}
.patient-picker-hide-options .vs__dropdown-menu {
  display: none;
}
.patient-picker-hide-options.vs--open .vs__dropdown-toggle {
  border-bottom-color: #4044e6 !important;
  border-bottom-width: 2px;
}
</style>
